import {TopNav} from "../../components/TopNavigation";
import {
  AppLayout,
  BreadcrumbGroup,
  Button,
  ButtonDropdown,
  Container,
  ContentLayout,
  FormField,
  Header,
  Input,
  Link,
  Select,
  SpaceBetween,
  Textarea,
  TextContent,
  Toggle
} from "@cloudscape-design/components-themed";
import {Navigation} from "../../components/Navigation";
import React, {useCallback, useMemo, useState} from "react";
import {debounce} from "lodash";
import {NextPageContext} from "next";
import {useRouter} from "next/router";
import QRCode from "react-qr-code";
import {OptionDefinition} from "@cloudscape-design/components-themed/internal/components/option/interfaces";

enum CopyStatus {READY = "Copy", COPYING = "Copying", COPIED = "Copied"}

export function getServerSideProps(ctx: NextPageContext) {
  return {
    props: {}
  }
}

function Breadcrumbs() {
  const breadcrumbItems = [
    {
      text: 'Home',
      href: '/',
    },
    {
      text: 'QR Code Tools',
      href: '/qr',
    },
    {
      text: 'Wi-Fi QR Code Generator',
      href: '/qr/wifi',
    },
  ];
  return <BreadcrumbGroup items={breadcrumbItems} expandAriaLabel="Show path" ariaLabel="Breadcrumbs"/>;
}

const wifiSecurityOptions: OptionDefinition[] = [
  {label: 'WPA/WPA2/WPA3', value: 'WPA'},
  {label: 'WEP', value: 'WEP'},
  {label: 'None', value: 'none'},
];

const wifiToString = (ssid: string, security: string, password?: string, hidden?: boolean) => {
  return `WIFI:S:${ssid};T:${security};P:${password ?? ""};${hidden ? `${hidden}` : ""};`
}

export default function Page() {
  const router = useRouter();
  const [networkName, setNetworkName] = useState("");
  const [securityMode, setSecurityMode] = useState(wifiSecurityOptions[0]);
  const [password, setPassword] = useState("");
  const [hidden, setHidden] = useState(false);
  // const [result, setResult] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  // const setValueDebounce = debounce((data) => {
  //   const value = wifiToString(networkName, securityMode.value ?? "", password);
  //   setValue(value);
  // }, 500);

  const result = useMemo(() => {
    const qrCodeString =  wifiToString(networkName, securityMode.value ?? "", password, hidden);
    setProcessing(false);
    return qrCodeString;
  }, [networkName, securityMode.value, password, hidden]);

  const [copyStatus, setCopyStatus] = useState(CopyStatus.READY);
  const onCopyClicked = useCallback((text: string) => {
    setCopyStatus(CopyStatus.COPYING);
    navigator.clipboard.writeText(text);
    setTimeout(() => {
      setCopyStatus(CopyStatus.COPIED);
    }, 200);
    setTimeout(() => {
      setCopyStatus(CopyStatus.READY);
    }, 1000);
  }, []);

  return (
    <>
      <TopNav/>
      <AppLayout
        content={
          <ContentLayout
            header={
              <Header
                variant="h1"
                info={
                  <Link variant="info" ariaLabel={'Information about text converter.'}/>
                }
              >
                Wi-Fi QR Code Generator
              </Header>
            }
          >
            <SpaceBetween size="m">
              <Container>
                <SpaceBetween size="m">
                  <SpaceBetween size="s">
                    <SpaceBetween size="s" direction="horizontal">
                      <Header variant="h2">Wi-Fi Setup</Header>
                    </SpaceBetween>
                    <FormField label="Network Name" description="Name of the Wi-Fi Network(e.g. FastNet5GHz)">
                      <Input
                        placeholder={'SSID'}
                        value={networkName}
                        onChange={(event) => {
                          setProcessing(true);
                          setNetworkName(event.detail.value);
                        }}
                      />
                    </FormField>
                    <FormField label="Security Mode"
                               description="Choose WPA / WPA2 / WPA3 (recommended) for the best security, or WEP for compatibility">
                      <Select
                        placeholder={'Wi-Fi Security'}
                        options={wifiSecurityOptions}
                        onChange={(event) => {
                          setProcessing(true);
                          setSecurityMode(event.detail.selectedOption ?? wifiSecurityOptions[0]);
                        }}
                        selectedOption={securityMode}
                      />
                    </FormField>
                    {securityMode.value !== "none" &&
                      <FormField label="Password" description="Wi-Fi Network Password">
                        <Input
                          placeholder={`${securityMode.label} Password`}
                          value={password}
                          onChange={(event) => {
                            setProcessing(true);
                            setPassword(event.detail.value);
                          }}
                          type="password"
                        />
                      </FormField>
                    }
                  </SpaceBetween>
                  <FormField label="Hidden Network" description="Check this box if the network is hidden">
                    <Toggle
                      onChange={(event) => setHidden(event.detail.checked)}
                      checked={hidden}
                    >
                      Hidden
                    </Toggle>
                  </FormField>
                  <SpaceBetween size="s" direction="horizontal">
                    <Button variant="primary" iconName="multiscreen"
                            loading={processing}>Convert</Button>
                    {error && <TextContent>{error}</TextContent>}
                  </SpaceBetween>
                  <SpaceBetween size="s">
                    <SpaceBetween size="s" direction="horizontal">
                      <Header variant="h2">Result</Header>
                    </SpaceBetween>
                    <SpaceBetween size="s" direction="horizontal">
                      <QRCode value={result}/>
                      <SpaceBetween size="s">
                        <ButtonDropdown items={[
                          {text: "Save as PNG", id: "png"},
                          {text: "Save as JPG", id: "jpg"},
                          {text: "Save as SVG", id: "svg"},
                        ]}>Save As</ButtonDropdown>
                      </SpaceBetween>
                    </SpaceBetween>
                    <SpaceBetween size="s" direction="horizontal">
                      <Textarea readOnly value={result}/>
                      <Button
                        iconName="copy"
                        onClick={() => onCopyClicked(result)}
                        loading={copyStatus == CopyStatus.COPYING}
                        disabled={copyStatus == CopyStatus.COPIED}
                      >
                        {copyStatus}
                      </Button>
                    </SpaceBetween>
                  </SpaceBetween>
                </SpaceBetween>
              </Container>
            </SpaceBetween>
          </ContentLayout>
        }
        headerSelector="#header"
        breadcrumbs={<Breadcrumbs/>}
        navigation={<Navigation/>}
        // tools={toolsContent}
        // toolsOpen={toolsOpen}
        // onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        // ariaLabels={appLayoutLabels}
        // notifications={<Notifications />}
      />
    </>

  )
}
